import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Bell, RefreshCw, Send, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPushTestDevices, sendPushTest } from "@/lib/mobile-push.functions";
import { mobilePushMessage, PUSH_KINDS, testPushTrade, type PushAudience, type PushKind } from "@/lib/mobile-push-copy";

export const Route = createFileRoute("/_authenticated/admin/push-tests")({
  head: () => ({ meta: [
    { title: "Notification Tests | XAU Pro Academy Admin" },
    { name: "description", content: "Private device notification testing for XAU Pro Academy." },
    { property: "og:title", content: "Notification Tests | XAU Pro Academy Admin" },
    { property: "og:description", content: "Private mobile notification testing desk." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex, nofollow" },
  ] }),
  component: PushTests,
});

function PushTests() {
  const read = useServerFn(getPushTestDevices);
  const send = useServerFn(sendPushTest);
  const q = useQuery({ queryKey: ["admin-own-push-devices"], queryFn: () => read() });
  const [deviceId, setDeviceId] = useState("");
  const [engine, setEngine] = useState<"intraday" | "swing">("intraday");
  const [side, setSide] = useState<"BUY" | "SELL">("BUY");
  const [audience, setAudience] = useState<PushAudience>("paid");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const devices = q.data?.devices ?? [];
  const selected = devices.find(d => d.id === deviceId) ?? devices[devices.length - 1];
  const test = async (kind: PushKind | "WELCOME") => {
    if (!selected || busy) return;
    setBusy(true); setStatus("");
    try {
      await send({ data: { deviceId: selected.id, engine, side, audience, kind } });
      setStatus("Accepted by OneSignal. Confirm receipt on your selected phone.");
    } catch (e) { setStatus(e instanceof Error ? e.message : "Test could not be sent."); }
    finally { setBusy(false); }
  };
  return <div className="space-y-8">
    <header className="border-b border-border pb-6">
      <p className="font-mono text-xs uppercase text-gold">Private device testing</p>
      <h1 className="mt-2 font-display text-2xl font-semibold">Notification Tests</h1>
      <p className="mt-2 text-sm text-muted-foreground">Synthetic trade scenarios. Live member notifications remain disabled.</p>
    </header>
    <section className="space-y-4" aria-label="Test device">
      <div className="flex items-center justify-between gap-3"><h2 className="flex items-center gap-2 text-lg"><Smartphone className="size-5 text-gold" />Your device</h2>
        <Button variant="ghost" size="icon" aria-label="Refresh devices" onClick={() => void q.refetch()}><RefreshCw /></Button></div>
      {q.isError ? <p role="alert" className="text-sm text-destructive">Device list could not be loaded.</p> : q.isPending ? <p className="text-sm text-muted-foreground">Reading devices...</p> : !devices.length ? <p className="text-sm text-muted-foreground">No linked phone yet. Install the test app, sign in and enable notifications.</p> :
        <select aria-label="Selected phone" value={selected?.id ?? ""} onChange={e => setDeviceId(e.target.value)} className="h-10 w-full max-w-lg rounded-md border border-input bg-background px-3 text-sm">
          {devices.map((d, i) => <option key={d.id} value={d.id}>{d.platform.toUpperCase()} · Device {i + 1} · {new Date(d.last_seen_at).toLocaleString()}</option>)}
        </select>}
      <Button variant="outlineGold" disabled={!selected || busy || !q.data?.configured} onClick={() => void test("WELCOME")}><Bell />Send Connection Test</Button>
    </section>
    <section className="space-y-4" aria-label="Notification scenarios">
      <div className="flex flex-wrap gap-5">
        <Control label="Position" values={["intraday", "swing"]} value={engine} onChange={setEngine} />
        <Control label="Direction" values={["BUY", "SELL"]} value={side} onChange={setSide} />
        <Control label="Audience" values={["paid", "free", "showcase"]} value={audience} onChange={setAudience} />
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {PUSH_KINDS.map(kind => {
          const message = mobilePushMessage(testPushTrade(engine, kind, side), audience);
          return <article key={kind} className="flex flex-col rounded-lg border border-border bg-card p-5">
            <span className="font-mono text-[10px] uppercase text-muted-foreground">Test · {audience} · {kind}</span>
            <h3 className="mt-3 font-semibold">{message.title}</h3>
            <p className="mt-2 flex-1 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{message.body}</p>
            <Button variant="outline" className="mt-5 w-full" disabled={!selected || busy || !q.data?.configured} onClick={() => void test(kind)}><Send />Send Test</Button>
          </article>;
        })}
      </div>
    </section>
    {status && <p role="status" className="border-t border-border pt-4 text-sm text-gold">{status}</p>}
  </div>;
}

function Control<T extends string>({ label, values, value, onChange }: { label: string; values: T[]; value: T; onChange: (v: T) => void }) {
  return <div><p className="mb-2 font-mono text-[10px] uppercase text-muted-foreground">{label}</p>
    <div className="flex gap-1" role="group" aria-label={label}>{values.map(v => <Button key={v} size="sm" variant={value === v ? "outlineGold" : "ghost"} aria-pressed={value === v} onClick={() => onChange(v)}>{v === "showcase" ? "Free showcase" : v}</Button>)}</div>
  </div>;
}